import { Helper } from "../helpers/helper";
import { ImisClientContext, ImisResponseItems } from "../models/imis.models";
import { RestUtility } from "./rest.utility";
import { UrlUtility } from "./url.utility";

/**
 * Helpers for reading iMIS's client-side page context (selected party id, timezone, version) exposed via the current ASP.NET page.
 */
export class ContextUtility {
    /**
     * Reads and parses the iMIS `__ClientContext` hidden field embedded in the current page into a strongly typed object.
     * @returns the parsed client context, or a default empty {@link ImisClientContext} if the field is missing or empty
     */
    public static getImisClientContext(): ImisClientContext {
        let clientContext = new ImisClientContext();

        let jClientContext = jQuery("#__ClientContext");
        if (jClientContext.length > 0) {
            let clientContextString = jClientContext.val().toString();
            if (clientContextString.length > 0) {
                clientContext = JSON.parse(clientContextString);
            }
        }

        return clientContext;
    }

    /**
     * Resolves the currently selected party/record id, preferring the `id` URL query parameter when `useUrl` is true and the parameter has a value, otherwise falling back to the page's client context.
     * @param useUrl when true, use the `id` URL parameter if present; otherwise (or if the URL parameter is empty) fall back to the client context's `selectedPartyId`. Defaults to false.
     * @returns the selected iMIS id
     */
    public static getSelectedImisId(useUrl = false): string {
        let selectedId = UrlUtility.findUrlParameter("id");
        let clientContext = this.getImisClientContext();
        
        if (!useUrl || Helper.isStringNullOrEmpty(selectedId)) {
            selectedId = clientContext.selectedPartyId;
        }

        return selectedId;
    }

    /**
     * Converts a local `Date` into the equivalent date/time in the iMIS application's configured timezone, by combining the local UTC offset with iMIS's `appTimeZoneOffset` from the client context.
     * @param localDateTime the local date/time to convert; defaults to the current date/time
     * @returns the date/time adjusted to the iMIS application's timezone
     */
    public static getDateTimeInImisTimezone(localDateTime: Date = new Date()): Date {
        let dateTimeInImisTimezone: Date;

        let localDateTimeOffsetFromUtcHours = localDateTime.getTimezoneOffset() * 60 * 1000;
        let appTimeZoneOffsetHours = this.getImisClientContext().appTimeZoneOffset;

        let totalOffset = localDateTimeOffsetFromUtcHours + appTimeZoneOffsetHours;        
        dateTimeInImisTimezone = new Date(localDateTime.getTime() + totalOffset);
        
        return dateTimeInImisTimezone;
    }

    /**
     * Queries iMIS's `System_Control.Version` system parameter to determine the iMIS version and whether the site is a cloud (20.3.x) deployment.
     * @returns object with the version string (empty if not found) and an `isCloud` flag
     */
    public static async getImisVersion(): Promise<{ version: string, isCloud: boolean }> {
        let imisVersion = { version: "", isCloud: false };

        let systemParamsResponse = await RestUtility.restApiGetData<ImisResponseItems<any>>("/api/SystemParam", { ParameterName: "System_Control.Version" });
        if (systemParamsResponse != null && systemParamsResponse.Items.length > 0) {
            imisVersion.version = systemParamsResponse.Items[0].ShortValue;
            imisVersion.isCloud = !Helper.isStringNullOrEmpty(imisVersion.version) && imisVersion.version.startsWith("20.3");
        }

        return imisVersion;
    }

}